import clsx from "clsx";
import { graphql, Link } from "gatsby";
import React, { FC, MouseEventHandler, PropsWithChildren, useCallback, useMemo } from "react";

import { useDataLayer } from "../../../hooks/use-data-layer";
import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { Optional } from "../../../types/optional";
import { getTabQueryParam } from "../../../utils/tabs";
import { Button, ButtonProps } from "../button";

export interface CtaProps
  extends Optional<Queries.CtaDataFragment, "anchor" | "tab" | "eventName" | "eventProperties">,
    Pick<ButtonProps, "variant" | "fullWidth" | "size" | "disabled"> {
  className?: string;
  buttonClassName?: string;
  onClick?: MouseEventHandler;
}

export const Cta: FC<PropsWithChildren<CtaProps>> = ({
  variant,
  fullWidth,
  label,
  externalUrl,
  pageLink,
  className,
  children,
  buttonClassName,
  onClick: _onClick,
  tab,
  anchor,
  eventName,
  eventProperties,
  ...otherButtonProps
}) => {
  const tabParams = useMemo(() => {
    if (!pageLink?.id || !tab?.title) return null;
    const tabsSection = tab.tabssection?.[0];
    if (!tabsSection) return null;
    const tabIndex = tabsSection.tabs?.findIndex((t) => t?.id === tab.id);
    if (!tabIndex) return null;
    return getTabQueryParam(tab.title, tabIndex, tabsSection.slug);
  }, [tab, pageLink]);

  const { pushEvent } = useDataLayer();
  const onClick: MouseEventHandler = useCallback(
    (e) => {
      if (eventName) {
        let eventProps: object = {};
        try {
          if (eventProperties?.internal.content) {
            eventProps = JSON.parse(eventProperties.internal.content);
          }
        } catch (e) {
          console.error(e);
        }
        pushEvent({
          event: eventName,
          ...eventProps,
        });
      }
      if (_onClick) _onClick(e);
    },
    [_onClick, pushEvent, eventName, eventProperties]
  );

  const childElement = children ?? (
    <Button
      variant={variant}
      fullWidth={fullWidth}
      type="button"
      className={buttonClassName}
      {...otherButtonProps}
    >
      {label}
    </Button>
  );

  const ctaClass = clsx(className, { "w-full": fullWidth });
  const pageLinkPath = usePageFullPath(pageLink?.id);

  const pageLinkWithParams = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (tabParams) {
      searchParams.set(tabParams.key, tabParams.value);
    }
    const pageLinkWithParams = [pageLinkPath, searchParams.toString()].filter(Boolean).join("?");
    const pageLinkWithAnchor = [pageLinkWithParams, anchor?.slug].filter(Boolean).join("#");
    return pageLinkWithAnchor;
  }, [pageLinkPath, anchor, tabParams]);

  return otherButtonProps.disabled ? (
    childElement
  ) : pageLinkWithParams ? (
    <Link to={pageLinkWithParams} className={ctaClass} onClick={onClick}>
      {childElement}
    </Link>
  ) : (
    <a
      href={externalUrl ?? ""}
      className={ctaClass}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
    >
      {childElement}
    </a>
  );
};

export const query = graphql`
  fragment CtaData on ContentfulCta {
    label
    externalUrl
    pageLink {
      id
    }
    tab {
      id
      title
      tabssection {
        id
        slug
        tabs {
          id
        }
      }
    }
    anchor {
      ...AnchorData
    }
    eventName
    eventProperties {
      internal {
        content
      }
    }
  }
`;
